import { getLogger } from "@expert/logging";
import { createImmerStore } from "@expert/state-utils";

import type { LongFormAiEventPayload, PitchType } from "../events";
import type { VhdpPlanData } from "../PlanDetails/vhdpPlanData";
import type { BeatStatus, Narrative } from "../types";

import { getIsProd } from "../../../config";
import { sendSalesErrorMetric } from "../../../shared-utils";
import { toPitchType } from "../events";
import { filterNarrativeForGuidedBrowsing } from "../fallbackNarrative";
import { isSameNarrative } from "../types";

const logger = getLogger({ module: "useLongFormAiStore", supportTeam: "sales" });

/**
 * Long Form AI session + reference state (openspec long-form-ai, tasks 1.12 / 1.21 / 5.5).
 *
 * Two lifecycles, two shapes:
 *  - `sessions` — per-session narrative + beat progress, populated by `receiveNarrativeEvent` exactly
 *    as if the `LongFormAiEvent` had arrived over the WebSocket. Beat status is keyed by STABLE beat id
 *    (not index) so a replacement narrative preserves covered/skipped across a content swap (1.21).
 *  - `planDetailsBySku` — the "View plan details" reference content, keyed by SKU. Static (seeded from
 *    `vhdpPlanData`) at first; this is the shape Contentful will feed later.
 *
 * The flag alone selects the surface (see `shouldShowLongFormAi`) — there is no in-session fallback to
 * the checklist, so no `surface` state lives here.
 */

/**
 * Plan-details reference content, keyed by SKU. `VhdpPlanData` is the only shape today (VHDP is the
 * pilot offer); the alias keeps the per-SKU store API honest as more product shapes / Contentful land.
 */
export type PlanData = VhdpPlanData;

export interface LongFormAiSessionState {
    narrative: Narrative | null;
    /**
     * A replacement narrative that arrived for the sale already on screen, held until the expert loads
     * it from the "New script available" banner. Never applied automatically — swapping the script
     * while it is being read aloud is the hazard. Latest wins: a second arrival overwrites an unloaded
     * one rather than queueing, so the expert always loads the freshest.
     *
     * Staged in the STORE, unlike DSO's equivalent gate, which compares the store's `latestData`
     * against a `useState` snapshot in `useDynamicSmartOffer`. That snapshot resets on unmount, so
     * DSO's gate is lost on a remount and the newest content then shows by itself. `ProductSaleFlow`
     * remounts this panel whenever the selected product changes, which would be exactly the mid-call
     * swap the banner exists to prevent — hence store state, not component state.
     */
    pendingNarrative: Narrative | null;
    /** The producer's `pitchType` for `narrative`; `null` for the client fallback, or when the producer
     *  omits it or sends a member `PITCH_TYPES` doesn't know. An identical-copy redelivery is deduped
     *  before this is touched, so it keeps the first delivery's value. */
    pitchType: PitchType | null;
    /** The `pitchType` for `pendingNarrative`, promoted with it. */
    pendingPitchType: PitchType | null;
    /** `true` for the client fallback, `false` for producer-delivered. Feeds the `isFallback`
     *  telemetry dimension; `true` when there is no narrative, the fallback being V0's only source. */
    isFallback: boolean;
    /** Whether the funnel's OPEN event has been dispatched for this sale. Lives here, not in a panel
     *  ref, so a remount within the same session cannot double-count it — same reason as the two
     *  `awaiting*` flags below. Reset by a new SKU, which is a new sale. */
    narrativeShown: boolean;
    /** The offer this narrative is for — the key into `planDetailsBySku`, and half of the (sessionId,
     *  sku) narrative identity. */
    sku: string | null;
    /** Per-beat progress, keyed by beat id. A missing id is treated as "pending". */
    statuses: Record<string, BeatStatus>;
    /** The beat currently being worked. */
    currentBeatId: string | null;
    /**
     * After the enrollment link is sent, the Enroll step holds its "waiting to enroll" state until
     * the async enrollment-event flips the surface to success. Durable (session-scoped store state,
     * not local React state) so it survives the panel unmounting (a tab switch, a remount, etc.)
     * instead of silently resurrecting the send form and letting a second real link go out.
     */
    awaitingEnrollment: boolean;
    /**
     * After the guided-browsing link is sent, the Guide step holds its "live" state until the expert
     * clicks Continue. Durable for the same reason as `awaitingEnrollment` — a remount must not
     * resurrect the send form and let a second co-browse link go out.
     */
    awaitingGuide: boolean;
}

interface LongFormAiStore {
    sessions: Record<string, LongFormAiSessionState>;
    planDetailsBySku: Record<string, PlanData>;
    getSession: (sessionId: string) => LongFormAiSessionState;
    getPlanDetails: (sku: string) => PlanData | undefined;
    /** Record an inbound narrative (WS, or the harness seed button). Callers pass the RAW narrative —
     *  the guided-browsing filter is applied HERE so every producer behaves the same. */
    receiveNarrativeEvent: (event: LongFormAiEventPayload, isGuidedBrowsing: boolean) => void;
    /** Promote `pendingNarrative` to the displayed one (the banner's "Show latest"). No-op when none. */
    applyPendingNarrative: (sessionId: string) => void;
    /** Record that the funnel's OPEN event has been dispatched for this session. */
    markNarrativeShown: (sessionId: string) => void;
    /** Seed/replace the plan-details reference content for a SKU (static now, Contentful later). */
    setPlanDetails: (sku: string, data: PlanData) => void;
    markRead: (sessionId: string, beatId: string) => void;
    markSkipped: (sessionId: string, beatId: string) => void;
    /** Re-open a covered/skipped beat — back to pending, cursor lands on it (guide re-open, #7180). */
    reopenBeat: (sessionId: string, beatId: string) => void;
    /** Load the static client fallback narrative into the session (V0 has no producer, #7180). */
    loadFallbackNarrative: (sessionId: string, narrative: Narrative, sku: string) => void;
    /** Hold / release the Enroll step's post-send "waiting to enroll" state (durable across unmount). */
    setAwaitingEnrollment: (sessionId: string, awaiting: boolean) => void;
    /** Hold / release the Guide step's post-send "live" state (durable across unmount). */
    setAwaitingGuide: (sessionId: string, awaiting: boolean) => void;
    reset: (sessionId: string) => void;
}

const emptySession: LongFormAiSessionState = {
    narrative: null,
    pendingNarrative: null,
    pitchType: null,
    pendingPitchType: null,
    isFallback: true,
    narrativeShown: false,
    sku: null,
    statuses: {},
    currentBeatId: null,
    awaitingEnrollment: false,
    awaitingGuide: false,
};

/** Beat ids in render order (Transition → … → Close, beats within each step). */
const orderedBeatIds = (narrative: Narrative): string[] =>
    narrative.steps.flatMap((step) => step.beats.map((beat) => beat.id));

/** First beat still pending, else the last beat (all done), else null. */
const cursorFor = (narrative: Narrative, statuses: Record<string, BeatStatus>): string | null => {
    const ids = orderedBeatIds(narrative);
    return ids.find((id) => (statuses[id] ?? "pending") === "pending") ?? ids[ids.length - 1] ?? null;
};

/**
 * Session state for a narrative becoming the DISPLAYED one, from either source. Covered/skipped beat
 * statuses and the two post-send waits carry over for the SAME sale — a different SKU is a different
 * sale and starts fresh. Beat ids absent from the new narrative are dropped; new ids start pending.
 */
const displaying = (
    prev: LongFormAiSessionState | undefined,
    narrative: Narrative,
    sku: string,
    isFallback: boolean,
    pitchType: PitchType | null,
): LongFormAiSessionState => {
    // Captured as the session itself (not a boolean) so the reads below narrow without re-checking.
    const sameSale = prev && prev.sku === sku ? prev : undefined;
    const carried: Record<string, BeatStatus> = {};
    // Guarded on `sameSale`, not `prev`: beat ids are semantic and repeat across products, so keying
    // off `prev` marked a new sale's beats covered from the previous SKU's progress.
    if (sameSale) {
        for (const id of orderedBeatIds(narrative)) {
            const status = sameSale.statuses[id];
            if (status && status !== "pending") carried[id] = status;
        }
    }
    return {
        narrative,
        pendingNarrative: null,
        pitchType,
        pendingPitchType: null,
        isFallback,
        narrativeShown: sameSale?.narrativeShown ?? false,
        sku,
        statuses: carried,
        currentBeatId: cursorFor(narrative, carried),
        // Carrying the waits matters for correctness, not just polish: a replacement landing after a
        // link was sent would otherwise revert the step to its send form and let a second real link go.
        awaitingEnrollment: sameSale?.awaitingEnrollment ?? false,
        awaitingGuide: sameSale?.awaitingGuide ?? false,
    };
};

// In-memory only (no `persist`). Both slices are re-derivable on mount, so nothing needs to survive a
// reload: `sessions` is event-sourced (WS / the harness seed), and `planDetailsBySku` is re-seeded
// static reference content (the harness fixture now, Contentful later). Persisting either just wrote
// the (large) plan map to localStorage on every mutation for a value nothing ever read back — the
// store set `skipHydration` and isn't in any `persist.rehydrate()` list.
export const useLongFormAiStore = createImmerStore<LongFormAiStore>({
    devtools: { store: "long-form-ai-store", name: "workspace/sales-tools", isProd: getIsProd() },
})((set, get) => ({
    sessions: {},
    planDetailsBySku: {},

    getSession: (sessionId) => get().sessions[sessionId] ?? emptySession,
    getPlanDetails: (sku) => get().planDetailsBySku[sku],

    receiveNarrativeEvent: (event, isGuidedBrowsing) => {
        // Read before the producer runs so the report below stays out of the reducer.
        const existing = get().sessions[event.sessionId];
        // DROP a narrative for a SKU other than the sale on screen. This surface only ever shows one
        // product — `shouldShowLongFormAi` gates on the SELECTED product being the pilot SKU and the
        // panel stays mounted — so the displayed SKU never legitimately changes under it, and the only
        // production route here is a mis-keyed or mis-routed producer event.
        //
        // Applying one was destructive with no expert action and no undo: the script swapped
        // mid-sentence, `displaying` reset the beat statuses and sent the cursor back to beat 1, the
        // plan data behind "View plan details" is keyed on the session SKU so it vanished, and the
        // force-clear of the waits re-armed the send buttons while a real link was outstanding —
        // re-opening the duplicate-link gap `ff999a29a` and `a0b5c1235` were written to close. All of it
        // under a header still naming the pilot product, with Decline and both send handlers still
        // acting on the selected (pilot) product — so the expert would read one product's script and
        // enroll the customer in another.
        //
        // NOTE this narrows the `events.ts` "identity is (sessionId, sku)" contract for this surface:
        // `displaying`'s SKU-change handling stays correct and covered, it is simply no longer reachable
        // from the WebSocket path. Revisit when a second SKU is real.
        if (existing?.narrative && existing.sku && existing.sku !== event.sku) {
            logger.warn(
                { sessionId: event.sessionId, displayedSku: existing.sku, eventSku: event.sku },
                "[LFA] dropped a narrative for a different SKU than the sale on screen",
            );
            void sendSalesErrorMetric("LongFormAiNarrativeSkuMismatch");
            return;
        }
        set(
            (state) => {
                const prev = state.sessions[event.sessionId];
                // Filter to the guided-browsing enrollment path HERE so any producer (the harness
                // seed or a real WS) is consistent: GB on → keep Guide, drop Enroll; GB off → reverse.
                const narrative = filterNarrativeForGuidedBrowsing(event.narrative, isGuidedBrowsing);
                // A replacement for a sale already showing a PRODUCER script is STAGED behind the
                // banner, never applied — see `pendingNarrative`.
                //
                // Gated on `!prev.isFallback`, not merely "a narrative exists": `LongFormAiPanel` loads
                // the client fallback into this store as soon as it mounts, under the same SKU, so the
                // looser check staged the FIRST producer script too. No AI script would ever reach the
                // expert without a click, and every V0 funnel event would carry `isFallback: true`,
                // pinning the adoption dashboard at 100% fallback. The rule is "the first narrative
                // lands on its own" — and for that purpose the fallback is not a first narrative.
                if (prev?.narrative && !prev.isFallback) {
                    // At-least-once delivery: a redelivered identical message must not raise a banner
                    // for the script already on screen. DSO's gate compares content for the same reason
                    // (`useDynamicSmartOffer`'s `.equals(data)` term).
                    if (isSameNarrative(prev.narrative, narrative)) return;
                    prev.pendingNarrative = narrative;
                    prev.pendingPitchType = toPitchType(event.pitchType);
                    return;
                }
                state.sessions[event.sessionId] = displaying(
                    prev,
                    narrative,
                    event.sku,
                    false,
                    toPitchType(event.pitchType),
                );
            },
            false,
            "receiveNarrativeEvent",
        );
    },

    applyPendingNarrative: (sessionId) => {
        set(
            (state) => {
                const prev = state.sessions[sessionId];
                if (!prev?.pendingNarrative || !prev.sku) return;
                state.sessions[sessionId] = displaying(
                    prev,
                    prev.pendingNarrative,
                    prev.sku,
                    false,
                    prev.pendingPitchType,
                );
            },
            false,
            "applyPendingNarrative",
        );
    },

    markNarrativeShown: (sessionId) => {
        set(
            (state) => {
                const session = state.sessions[sessionId];
                if (session) session.narrativeShown = true;
            },
            false,
            "markNarrativeShown",
        );
    },

    setPlanDetails: (sku, data) => {
        set(
            (state) => {
                state.planDetailsBySku[sku] = data;
            },
            false,
            "setPlanDetails",
        );
    },

    markRead: (sessionId, beatId) => {
        set(
            (state) => {
                const session = state.sessions[sessionId];
                if (!session?.narrative) return;
                session.statuses[beatId] = "covered";
                session.currentBeatId = cursorFor(session.narrative, session.statuses);
            },
            false,
            "markRead",
        );
    },

    markSkipped: (sessionId, beatId) => {
        set(
            (state) => {
                const session = state.sessions[sessionId];
                if (!session?.narrative) return;
                session.statuses[beatId] = "skipped";
                session.currentBeatId = cursorFor(session.narrative, session.statuses);
            },
            false,
            "markSkipped",
        );
    },

    reopenBeat: (sessionId, beatId) => {
        set(
            (state) => {
                const session = state.sessions[sessionId];
                if (!session?.narrative) return;
                // Back to pending; cursorFor then lands the cursor on it (it's the earliest pending,
                // since everything before it was covered/skipped).
                session.statuses[beatId] = "pending";
                session.currentBeatId = cursorFor(session.narrative, session.statuses);
            },
            false,
            "reopenBeat",
        );
    },

    loadFallbackNarrative: (sessionId, narrative, sku) => {
        set(
            (state) => {
                // A producer narrative always outranks the fallback, and the reveal is a click whose
                // handler may have been painted before that narrative landed. Refusing here — where the
                // state is current by construction — is what actually makes the overwrite impossible.
                if (state.sessions[sessionId]?.narrative) return;
                state.sessions[sessionId] = displaying(state.sessions[sessionId], narrative, sku, true, null);
            },
            false,
            "loadFallbackNarrative",
        );
    },

    setAwaitingEnrollment: (sessionId, awaiting) => {
        set(
            (state) => {
                const session = state.sessions[sessionId];
                if (!session) return;
                session.awaitingEnrollment = awaiting;
            },
            false,
            "setAwaitingEnrollment",
        );
    },

    setAwaitingGuide: (sessionId, awaiting) => {
        set(
            (state) => {
                const session = state.sessions[sessionId];
                if (!session) return;
                session.awaitingGuide = awaiting;
            },
            false,
            "setAwaitingGuide",
        );
    },

    reset: (sessionId) => {
        set(
            (state) => {
                // Clear ONLY the per-session state. `planDetailsBySku` is SKU-keyed reference content
                // shared by every session on that SKU ("Two lifecycles, two shapes" above) — since the
                // narrative surface is VHDP-only, every session shares one key, so deleting it here
                // would wipe the plan data a concurrent session is still using.
                delete state.sessions[sessionId];
            },
            false,
            "reset",
        );
    },
}));
